import { startConsentMonitor } from '../shared/consentSignals'
import { logger } from './logger'
import { pauseTracking, resumeTracking } from './jitsuWrapper'

// --- State ---

// Customer-controlled flag. Default on. Can be toggled pre- and post-init.
let customerEnabled = true
// Visitor consent flag. False until a CMP explicitly denies.
let consentDenied = false
// Whether analytics listeners are currently running.
let trackingStarted = false

// Wired from index.ts after initJitsu succeeds.
let analyticsStart: (() => void) | null = null
let analyticsStop: (() => void) | null = null

// --- Core ---

function evaluateTracking(): void {
  const shouldTrack = customerEnabled && !consentDenied

  // Gate the jitsu wrapper from the consent state itself, as soon as it is known. The listeners
  // below can only start once index.ts has wired the controls, but calls that never go through the
  // listeners — the deanon embed's Identify/Deanonymization postMessages and productImpression() —
  // must be dropped whenever tracking is not allowed. Pausing only on the tracking → not-tracking
  // transition missed the case where consent is already denied at init: nothing had started, so
  // nothing was paused, and those calls (plus the queue flushed once p.js loaded) still reached
  // the ingest host.
  if (shouldTrack) {
    resumeTracking()
  } else {
    pauseTracking()
  }

  // Not wired yet — index.ts hasn't called registerAnalyticsControls
  if (!analyticsStart) return

  if (shouldTrack && !trackingStarted) {
    trackingStarted = true
    analyticsStart()
  } else if (!shouldTrack && trackingStarted) {
    trackingStarted = false
    analyticsStop?.()
  }
}

/**
 * Wires the analytics start/stop callbacks and triggers the first evaluation. Called from index.ts
 * after initJitsu succeeds.
 */
export function registerAnalyticsControls(start: () => void, stop: () => void): void {
  analyticsStart = start
  analyticsStop = stop
  evaluateTracking()
}

// --- Public API ---

/**
 * Detect CMP state and install the persistent consent listener. Called once during init, before
 * initJitsu. The shared monitor (also used by the deanonymization script) sets consentDenied from
 * the current state and keeps it updated from the dataLayer.
 */
export function initConsentHandling(): void {
  startConsentMonitor(
    denied => {
      consentDenied = denied
      // Applied to the jitsu wrapper right away — before initJitsu runs and flushes any calls
      // queued while p.js was loading.
      evaluateTracking()
    },
    message => logger.log(message)
  )
}

/**
 * Enable tracking. Safe to call before or after init. Takes effect immediately if init has already
 * run; otherwise applied on first evaluateTracking call.
 */
export function enableTracking(): void {
  customerEnabled = true
  evaluateTracking()
}

/**
 * Disable tracking. Safe to call before or after init. Stops listeners immediately if tracking is
 * active.
 */
export function disableTracking(): void {
  customerEnabled = false
  evaluateTracking()
}
